import { Check, Pencil, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "react-aria-components";

import { cn } from "@/utils/cn";

type EditableTextProps = {
  /** Render as a multi-line textarea instead of a single-line input. */
  multiline?: boolean;
  /** Persist the new value. Editing only renders when this is provided. */
  onSave?: (value: string) => void;
  /** Shown (muted) when the value is empty. */
  placeholder?: string;
  value: string;
};

/**
 * Inline-editable text used for the brief fields connected to working memory.
 * A pencil toggles edit mode; Enter (single-line) or the check button saves,
 * Escape or the X cancels. When `onSave` is omitted the text is read-only
 * (preview / mock mode).
 */
export function EditableText({
  multiline,
  onSave,
  placeholder,
  value,
}: EditableTextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing) {
      setDraft(value);
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing, value]);

  const commit = () => {
    const next = draft.trim();

    if (next !== value.trim()) onSave?.(next);
    setIsEditing(false);
  };

  const cancel = () => {
    setDraft(value);
    setIsEditing(false);
  };

  if (!onSave) {
    return (
      <p
        className={cn(
          "text-sm leading-relaxed text-text-secondary",
          !value && "italic text-text-tertiary",
        )}
      >
        {value || placeholder}
      </p>
    );
  }

  if (isEditing) {
    const sharedProps = {
      className: cn(
        "w-full rounded-md border border-border-brand bg-bg-primary px-2 py-1.5 text-sm text-text-primary outline-hidden",
      ),
      onChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => setDraft(event.target.value),
      onKeyDown: (
        event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => {
        if (event.key === "Escape") cancel();
        if (event.key === "Enter" && !multiline) {
          event.preventDefault();
          commit();
        }
      },
      placeholder,
      value: draft,
    };

    return (
      <div className="flex items-start gap-2">
        {multiline ? (
          <textarea {...sharedProps} ref={inputRef} rows={3} />
        ) : (
          <input {...sharedProps} ref={inputRef} type="text" />
        )}
        <div className="flex shrink-0 gap-1 pt-0.5">
          <Button
            aria-label="Save"
            className="cursor-pointer rounded-md p-1 text-fg-brand-primary outline-hidden transition-colors hover:bg-bg-primary-hover"
            onPress={commit}
          >
            <Check className="size-4" />
          </Button>
          <Button
            aria-label="Cancel"
            className="cursor-pointer rounded-md p-1 text-fg-quaternary outline-hidden transition-colors hover:bg-bg-primary-hover"
            onPress={cancel}
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex items-start gap-1.5">
      <p
        className={cn(
          "text-sm leading-relaxed text-text-secondary",
          !value && "italic text-text-tertiary",
        )}
      >
        {value || placeholder}
      </p>
      <Button
        aria-label="Edit"
        className="cursor-pointer rounded-md p-1 text-fg-quaternary opacity-0 outline-hidden transition-all hover:bg-bg-primary-hover hover:text-fg-brand-primary group-hover:opacity-100"
        onPress={() => setIsEditing(true)}
      >
        <Pencil className="size-3.5" />
      </Button>
    </div>
  );
}
